/**
 * /methodology page — rendered from the same source constants the calculator
 * uses at runtime.
 *
 * The whole point of the page is that a skeptical reader can reproduce any
 * number the site shows. To make that a real guarantee (and not a doc that
 * drifts from the code), every default and every citation on this page is
 * pulled from the exact constants the math engine and form load: change a
 * default in `defaults.ts` / `math/mortgage.ts` / `math/jurisdictions.ts`
 * and it re-renders here on the next build. If the two ever diverge, this
 * page is wrong — which is why it is a projection of the code, not prose.
 */

import {
  SCENARIO_DEFAULTS,
  SCENARIO_SOURCES,
  type SourceCitation,
} from "../defaults";
import { MORTGAGE_DEFAULTS, DEFAULT_SOURCES } from "../math/mortgage";
import { BREAK_EVEN_DEFAULTS } from "../math/breakEven";
import {
  JURISDICTIONS,
  type Jurisdiction,
  type TransferTax,
} from "../math/jurisdictions";

export function renderMethodology(): HTMLElement {
  const article = document.createElement("article");
  article.append(renderHeader());
  article.append(renderTOC());
  article.append(renderInputsSection());
  article.append(renderMetroTaxRuleSection());
  article.append(renderJurisdictionsSection());
  article.append(renderFormulasSection());
  article.append(renderWorkedExampleSection());
  article.append(renderVersionSection());
  article.append(renderRelatedSection());
  return article;
}

function renderHeader(): HTMLElement {
  const header = document.createElement("header");
  header.className = "content-header";
  header.innerHTML = `
    <h1>Methodology</h1>
    <p class="content-lede">
      Every default, formula, and source the calculator uses. Enough to
      reproduce any number yourself. If something on the results page cannot
      be re-derived from what is on this page, it is a bug — please tell us.
    </p>
  `;
  return header;
}

function renderTOC(): HTMLElement {
  const nav = document.createElement("nav");
  nav.className = "toc";
  nav.setAttribute("aria-label", "On this page");
  nav.innerHTML = `
    <h2>On this page</h2>
    <ol>
      <li><a href="#inputs">Default inputs and their sources</a></li>
      <li><a href="#metro-property-tax">Property tax on the metro pages</a></li>
      <li><a href="#jurisdictions">Transaction-friction by jurisdiction</a></li>
      <li><a href="#formulas">Formulas</a></li>
      <li><a href="#example">Worked example</a></li>
      <li><a href="#version">Versioning</a></li>
      <li><a href="#related">Where each assumption is explained at length</a></li>
    </ol>
  `;
  return nav;
}

interface DefaultRow {
  key: string;
  label: string;
  value: string;
  source: SourceCitation;
}

function renderInputsSection(): HTMLElement {
  const section = document.createElement("section");
  section.setAttribute("aria-labelledby", "inputs");
  section.innerHTML = `
    <h2 id="inputs">Default inputs and their sources</h2>
    <p>
      Every value in the table below is the pre-filled default the calculator
      loads on first visit. Each is editable — override any of them in the
      form and the answer updates live. The "source" column is the same
      citation you see in the tooltip beside each field.
    </p>
  `;

  const rows: DefaultRow[] = [
    {
      key: "monthlyRent",
      label: "Monthly rent",
      value: formatCurrency(SCENARIO_DEFAULTS.monthlyRent) + "/mo",
      source: SCENARIO_SOURCES.monthlyRent,
    },
    {
      key: "homePrice",
      label: "Home price",
      value: formatCurrency(SCENARIO_DEFAULTS.homePrice),
      source: SCENARIO_SOURCES.homePrice,
    },
    {
      key: "downPaymentPercent",
      label: "Down payment",
      value: `${MORTGAGE_DEFAULTS.downPaymentPercent}%`,
      source: DEFAULT_SOURCES.downPaymentPercent,
    },
    {
      key: "annualInterestRatePercent",
      label: "Mortgage rate (30-yr fixed)",
      value: `${MORTGAGE_DEFAULTS.annualInterestRatePercent}%`,
      source: DEFAULT_SOURCES.annualInterestRatePercent,
    },
    {
      key: "termYears",
      label: "Mortgage term",
      value: `${MORTGAGE_DEFAULTS.termYears} years`,
      source: DEFAULT_SOURCES.termYears,
    },
    {
      key: "horizonYears",
      label: "Decision horizon (years staying)",
      value: `${SCENARIO_DEFAULTS.horizonYears} years`,
      source: SCENARIO_SOURCES.horizonYears,
    },
    {
      key: "annualHomeAppreciationPercent",
      label: "Home appreciation",
      value: `${SCENARIO_DEFAULTS.annualHomeAppreciationPercent}%/yr`,
      source: SCENARIO_SOURCES.annualHomeAppreciationPercent,
    },
    {
      key: "annualRentInflationPercent",
      label: "Rent inflation",
      value: `${SCENARIO_DEFAULTS.annualRentInflationPercent}%/yr`,
      source: SCENARIO_SOURCES.annualRentInflationPercent,
    },
    {
      key: "annualDiscountRatePercent",
      label: "Investment return (discount rate)",
      value: `${SCENARIO_DEFAULTS.annualDiscountRatePercent}%/yr`,
      source: SCENARIO_SOURCES.annualDiscountRatePercent,
    },
    {
      key: "annualPropertyTaxRatePercent",
      label: "Property tax rate",
      value: `${BREAK_EVEN_DEFAULTS.annualPropertyTaxRatePercent}%/yr`,
      source: SCENARIO_SOURCES.annualPropertyTaxRatePercent,
    },
    {
      key: "annualHomeInsurance",
      label: "Home insurance",
      value: formatCurrency(BREAK_EVEN_DEFAULTS.annualHomeInsurance) + "/yr",
      source: SCENARIO_SOURCES.annualHomeInsurance,
    },
    {
      key: "annualMaintenanceRatePercent",
      label: "Maintenance reserve",
      value: `${BREAK_EVEN_DEFAULTS.annualMaintenanceRatePercent}% of home value/yr`,
      source: SCENARIO_SOURCES.annualMaintenanceRatePercent,
    },
    {
      key: "monthlyHOA",
      label: "HOA / condo fees",
      value: BREAK_EVEN_DEFAULTS.monthlyHOA
        ? `${formatCurrency(BREAK_EVEN_DEFAULTS.monthlyHOA)}/mo`
        : "$0/mo (none by default)",
      source: SCENARIO_SOURCES.monthlyHOA,
    },
    {
      key: "annualRentersInsurance",
      label: "Renter's insurance",
      value: formatCurrency(BREAK_EVEN_DEFAULTS.annualRentersInsurance) + "/yr",
      source: SCENARIO_SOURCES.annualRentersInsurance,
    },
  ];

  const table = document.createElement("table");
  table.className = "defaults-table";
  const thead = document.createElement("thead");
  thead.innerHTML = `
    <tr>
      <th scope="col">Input</th>
      <th scope="col">Default</th>
      <th scope="col">Source</th>
    </tr>
  `;
  const tbody = document.createElement("tbody");
  for (const row of rows) {
    const tr = document.createElement("tr");
    const nameCell = document.createElement("td");
    nameCell.textContent = row.label;
    const valueCell = document.createElement("td");
    valueCell.className = "cell-value";
    valueCell.textContent = row.value;
    const sourceCell = document.createElement("td");
    sourceCell.append(renderSource(row.source));
    tr.append(nameCell, valueCell, sourceCell);
    tbody.append(tr);
  }
  table.append(thead, tbody);
  section.append(table);
  return section;
}

function renderSource(source: SourceCitation): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "source-cell";
  const label = document.createElement("strong");
  label.textContent = source.label;
  const note = document.createElement("span");
  note.className = "source-note";
  note.textContent = ` — ${source.note}`;
  wrap.append(label, note);
  if (source.url) {
    wrap.append(document.createTextNode(" "));
    const link = document.createElement("a");
    link.href = source.url;
    link.textContent = "View source";
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    wrap.append(link);
  }
  return wrap;
}

/**
 * The rule the metro pages follow for property tax (REN-77).
 *
 * This section exists because the rule was broken once and nothing on the site
 * would have caught it. Six geo pages shipped with two different methods —
 * three derived the rate from statutory millage on a buyer's basis, three
 * published the Census owner-average — and one of them carried a section
 * explaining why that owner-average is the wrong measure, cross-linked to
 * three siblings using it. Writing the rule down here is what makes the
 * seventh metro page reintroducing the split a visible contradiction rather
 * than an oversight.
 *
 * Deliberately not generated from METROS: this page is bundled for the
 * calculator, and pulling the geo dataset in would ship six metros' worth of
 * citation prose to every visitor for a rule that is four sentences long. The
 * per-metro derivations live on the metro pages, next to the numbers.
 */
function renderMetroTaxRuleSection(): HTMLElement {
  const section = document.createElement("section");
  section.setAttribute("aria-labelledby", "metro-property-tax");
  section.innerHTML = `
    <h2 id="metro-property-tax">Property tax on the metro pages</h2>
    <p>
      The calculator's default property-tax rate is a national average, shown
      in the table above and editable like everything else. Our per-metro
      pages replace it with a local figure, and there is one rule for how that
      figure is computed:
    </p>
    <blockquote class="method-rule">
      <p>
        The effective property-tax rate is computed on a <strong>new buyer's
        assessed basis</strong> — published millage or levy rates applied to
        that page's purchase price, net of the homestead relief a new
        owner-occupier can claim — and never on an owner-average ratio such as
        median taxes paid divided by median home value.
      </p>
    </blockquote>
    <p>
      The owner-average shortcut is tempting because the Census Bureau
      publishes both halves of it for every metro. It measures the wrong
      taxpayer wherever assessment is capped for incumbents and reset on sale:
      Florida's Save Our Homes cap and Texas's 10% homestead cap both make a
      long-tenured owner's bill a poor guide to a buyer's. It also drifts even
      where nothing is capped, because the survey window averages several years
      of levy rates and pairs them with a metro-wide, all-homes median value
      rather than the single-family price the page is actually pricing.
    </p>
    <p>
      So every metro page derives its rate, names the levying bodies, shows the
      arithmetic and the resulting annual dollar figure, and prints the Census
      ratio beside it as a contrast — including where the two agree. "We
      checked and it reconciles" and "we never checked" must not look the same
      on a page.
    </p>
  `;
  return section;
}

function renderJurisdictionsSection(): HTMLElement {
  const section = document.createElement("section");
  section.setAttribute("aria-labelledby", "jurisdictions");
  section.innerHTML = `
    <h2 id="jurisdictions">Transaction-friction by jurisdiction</h2>
    <p>
      Buying and selling a home carries one-off cash costs that a rent-vs-buy
      model has to bake in — otherwise the owner-side answer is silently
      optimistic. The calculator ships with four jurisdictions today; each
      specifies the three levers below. Pick a different jurisdiction in the
      "Assumptions" panel to switch the whole set at once.
    </p>
  `;

  for (const j of JURISDICTIONS) {
    section.append(renderJurisdictionCard(j));
  }
  return section;
}

function renderJurisdictionCard(j: Jurisdiction): HTMLElement {
  const card = document.createElement("div");
  card.className = "jurisdiction-card";

  const h = document.createElement("h3");
  h.textContent = `${j.name} (${j.currency})`;
  card.append(h);

  const dl = document.createElement("dl");
  dl.append(
    dt("Buyer closing costs"),
    dd(
      `${(j.buyerClosingCosts.percentOfValue * 100).toFixed(2)}% of purchase price` +
        (j.buyerClosingCosts.fixed
          ? ` + ${formatCurrency(j.buyerClosingCosts.fixed, j.currency)} fixed`
          : ""),
      j.sources.buyerClosingCosts,
    ),
    dt("Transfer tax / stamp duty"),
    dd(
      formatTransferTax(j.transferTaxOnPurchase, j.currency),
      j.sources.transferTaxOnPurchase,
    ),
    dt("Selling-agent commission"),
    dd(
      `${(j.sellerAgentCommission.percentOfValue * 100).toFixed(2)}% of sale price`,
      j.sources.sellerAgentCommission,
    ),
  );
  card.append(dl);
  return card;
}

function dt(text: string): HTMLElement {
  const el = document.createElement("dt");
  el.textContent = text;
  return el;
}

function dd(value: string, source: string): HTMLElement {
  const el = document.createElement("dd");
  const strong = document.createElement("span");
  strong.className = "dd-value";
  strong.textContent = value;
  const src = document.createElement("span");
  src.className = "dd-source";
  src.textContent = ` — ${source}`;
  el.append(strong, src);
  return el;
}

function formatTransferTax(tax: TransferTax, currency: string): string {
  if (tax.kind === "none") return "None at the modelled level.";
  const parts = tax.brackets.map((bracket) => {
    const rate = `${(bracket.marginalRate * 100).toFixed(2)}%`;
    if (bracket.upTo === null) return `${rate} above the top tier`;
    return `${rate} up to ${formatCurrency(bracket.upTo, currency)}`;
  });
  return `Marginal-rate schedule: ${parts.join("; ")}.`;
}

function renderFormulasSection(): HTMLElement {
  const section = document.createElement("section");
  section.setAttribute("aria-labelledby", "formulas");
  section.innerHTML = `
    <h2 id="formulas">Formulas</h2>

    <h3>Mortgage payment</h3>
    <p>Standard fixed-rate amortization:</p>
    <pre class="formula"><code>M = P × i(1+i)^n / ((1+i)^n − 1)</code></pre>
    <ul>
      <li><strong>M</strong> — fixed monthly principal-and-interest payment.</li>
      <li><strong>P</strong> — loan principal = home price − down payment.</li>
      <li><strong>i</strong> — monthly interest rate = annual rate / 12.</li>
      <li><strong>n</strong> — total monthly payments = term (years) × 12.</li>
    </ul>
    <p>
      Interest is computed on the remaining balance each month and rounded to
      the cent; principal is the remainder; the final month absorbs any
      rounding drift so the balance closes to exactly $0.00.
    </p>

    <h3>Opportunity-cost portfolio</h3>
    <p>
      Both renter and buyer start with identical wealth — the down payment
      plus buy-side transaction costs. The buyer spends it acquiring the home;
      the renter invests it at the discount rate. Each month, whoever has the
      cheaper housing bill deposits the difference into their own investment
      portfolio; both portfolios compound at the same discount rate. This
      keeps the comparison symmetric and prevents the model from silently
      discarding the buyer's late-horizon cash advantage.
    </p>
    <pre class="formula"><code>portfolio(t) = portfolio(t-1) × (1 + r/12) + deposit(t)</code></pre>

    <h3>Home value and equity</h3>
    <pre class="formula"><code>home_value(t) = home_price × (1 + g/12)^t
loan_balance(t) = from amortization schedule
equity(t) = home_value(t) − loan_balance(t)</code></pre>

    <h3>Transaction friction</h3>
    <pre class="formula"><code>buy_cost = closing_costs(price) + transfer_tax(price)
sell_cost(t) = commission × home_value(t)</code></pre>
    <p>
      <code>sell_cost</code> is applied hypothetically at every month, not
      only at the horizon — the "what if I sold today" framing is the honest
      one because it shows how long buying takes to overcome its own upfront
      friction. Toggle "I plan to hold indefinitely" in the assumptions panel
      to remove <code>sell_cost</code> entirely (forever-home framing).
    </p>

    <h3>Net worth</h3>
    <pre class="formula"><code>rent_nw(t) = renter_portfolio(t)
own_nw(t)  = equity(t) − sell_cost(t) + owner_portfolio(t)</code></pre>

    <h3>Break-even year</h3>
    <p>
      The smallest year boundary <em>Y</em> at which
      <code>own_nw(Y) ≥ rent_nw(Y)</code>. Returns nothing when owning never
      overtakes renting inside the chosen horizon — the results view says so
      explicitly and does not invent a fake break-even year.
    </p>
  `;
  return section;
}

function renderWorkedExampleSection(): HTMLElement {
  const section = document.createElement("section");
  section.setAttribute("aria-labelledby", "example");
  const price = SCENARIO_DEFAULTS.homePrice;
  const downPct = MORTGAGE_DEFAULTS.downPaymentPercent;
  const down = price * (downPct / 100);
  const loan = price - down;
  const rate = MORTGAGE_DEFAULTS.annualInterestRatePercent;
  const monthlyRate = rate / 100 / 12;
  const n = MORTGAGE_DEFAULTS.termYears * 12;
  const payment =
    (loan * monthlyRate * Math.pow(1 + monthlyRate, n)) /
    (Math.pow(1 + monthlyRate, n) - 1);

  section.innerHTML = `
    <h2 id="example">Worked example — the default US scenario</h2>
    <p>
      Plug the defaults from the table above into the mortgage formula and you
      get the same monthly payment the calculator shows. Reproducing this on
      the back of an envelope should take under a minute; if it doesn't match
      what you see in the results view, either the calculator has a bug or
      this page is out of date. Either way, tell us.
    </p>
    <ul>
      <li>Home price <strong>P<sub>home</sub></strong> = ${formatCurrency(price)}</li>
      <li>Down payment = ${downPct}% = ${formatCurrency(down)}</li>
      <li>Loan principal <strong>P</strong> = ${formatCurrency(loan)}</li>
      <li>Nominal annual rate = ${rate}% → monthly <strong>i</strong> = ${(monthlyRate * 100).toFixed(6)}%</li>
      <li>Term = ${MORTGAGE_DEFAULTS.termYears} years → <strong>n</strong> = ${n} months</li>
      <li>
        <strong>M</strong> = ${formatCurrency(loan)} × ${monthlyRate.toFixed(6)}(1+${monthlyRate.toFixed(6)})<sup>${n}</sup> /
        ((1+${monthlyRate.toFixed(6)})<sup>${n}</sup> − 1)
        ≈ <strong>${formatCurrency(payment)}/mo</strong>
      </li>
    </ul>
    <p>
      The break-even engine wraps this into the full month-by-month cashflow
      comparison described above. The complete source lives at
      <code>src/math/breakEven.ts</code>, <code>src/math/mortgage.ts</code>,
      <code>src/math/portfolio.ts</code>, and <code>src/math/transactions.ts</code>
      — each function is pure, deterministic, and covered by unit tests
      including a golden-number regression on this default scenario.
    </p>
  `;
  return section;
}

function renderVersionSection(): HTMLElement {
  const section = document.createElement("section");
  section.setAttribute("aria-labelledby", "version");
  section.innerHTML = `
    <h2 id="version">Versioning</h2>
    <p>
      Any change to a default value or a formula is a code change with a
      user-visible commit message — never silent. This page is generated from
      the same constants the calculator loads at runtime, so if the calculator
      updates, this page updates with it. There is no separate "content
      source" to drift out of sync.
    </p>
    <p>
      Suggested change to a default or a source? Open an issue or a pull
      request against the repository. We ship the calculator, but we do not
      own the truth — the citations do.
    </p>
  `;
  return section;
}

/**
 * REN-65: this page is the shortest possible statement of each assumption.
 * The long-form argument for every one of them lives in a /learn article, and
 * a reader who disagrees with a default deserves a path to the reasoning
 * rather than a dead end. Links are one-way here and back from the articles.
 */
function renderRelatedSection(): HTMLElement {
  const section = document.createElement("section");
  section.setAttribute("aria-labelledby", "related");
  section.innerHTML = `
    <h2 id="related">Where each assumption is explained at length</h2>
    <p>
      Each entry below argues one line of this page in full — where the number
      comes from, how wrong it can plausibly be, and what happens to the
      break-even year when you move it.
    </p>
    <ul class="related-links">
      <li>
        <a href="/learn/break-even-year-plain-english/">Break-even year, plain English</a>
        — what the crossing point in the "Break-even year" formula above actually means.
      </li>
      <li>
        <a href="/learn/opportunity-cost-invest-down-payment/">Opportunity cost: investing your down payment instead</a>
        — the reasoning behind the symmetric portfolio model.
      </li>
      <li>
        <a href="/learn/invest-down-payment-vs-buy/">What investment return should you assume?</a>
        — how we picked the default discount rate, and why it matters more than the mortgage rate.
      </li>
      <li>
        <a href="/learn/closing-costs-explained/">Closing costs, explained</a>
        and <a href="/learn/closing-costs-by-state-2026/">closing costs by state</a>
        — the buy-side transaction friction in the jurisdiction table.
      </li>
      <li>
        <a href="/learn/home-maintenance-1-percent-rule/">Is the 1% maintenance rule real?</a>
        — the source and the spread behind the maintenance reserve default.
      </li>
      <li>
        <a href="/learn/piti-plus-hoa-insurance/">Property tax, insurance, HOA: your real monthly cost</a>
        — the carrying costs stacked on top of principal and interest.
      </li>
      <li>
        <a href="/learn/pmi-explained/">PMI explained</a>
        and <a href="/learn/5-down-vs-20-down-math/">5% down vs 20% down</a>
        — what changes when the down-payment default does not fit you.
      </li>
      <li>
        <a href="/learn/rent-to-price-ratio/">Rent-to-price ratio</a>
        — a sanity check on the rent and home-price pair you entered.
      </li>
    </ul>
    <p>
      The full library is at <a href="/learn/">Learn</a>. When you are ready to
      put your own numbers in, go back to the
      <a href="/">rent-vs-buy calculator</a> — every default listed on this
      page is editable there, and the answer updates as you type.
    </p>
  `;
  return section;
}

function formatCurrency(value: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}
